import useLookupListData from "@app/data-hooks/use-lookup-list-data.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { useStore } from "@tanstack/react-store";
import { QueryStatusRouter, Select } from "bi-lib";
import { useEffect, useRef } from "react";

interface Props {
    id?: number;
    setOnSave: (onSave: () => Promise<unknown>) => void;
}

interface FormProps {
    activeUtilizationCodeId: number;
    selectedAssignments: number[];
}

export default function ProfileForm({ id, setOnSave }: Props) {
    const { utilizationCodesList } = useLookupListData();

    const { saveProfile } = useStaffData();

    const form = useForm({
        defaultValues: {
            activeUtilizationCodeId: 2,
            selectedAssignments: [],
        } as FormProps,
        onSubmit: async ({ value }) =>
            await saveProfile.mutateAsync({
                staffingId: id,
                utilId: value.activeUtilizationCodeId,
                assignments: value.selectedAssignments.length == 0 ? null : value.selectedAssignments.join(","),
            }),
    });

    const { activeUtilizationCodeId, selectedAssignments } = useStore(form.store, (state) => state.values);

    const { profileList } = useStaffData({ id, utilId: activeUtilizationCodeId });

    const ref = useRef<HTMLFormElement>(null);

    const handleSave = async () => {
        if (!ref.current?.reportValidity()) {
            return;
        }
        await form.handleSubmit();
    };

    useEffect(() => {
        setOnSave(handleSave);
    }, []);

    useEffect(() => {
        if (!profileList.data) {
            return;
        }
        form.setFieldValue(
            "selectedAssignments",
            profileList.data.filter((x) => x.isSelected).map((x) => x.assignmentId!),
        );
    }, [profileList.data]);

    const handleSelectionChange = (id: number) => {
        const tempAssignments = [...selectedAssignments];
        const index = tempAssignments.indexOf(id);
        if (index != -1) {
            tempAssignments.splice(index, 1);
        } else {
            tempAssignments.push(id);
        }
        form.setFieldValue("selectedAssignments", tempAssignments);
    };

    return (
        <form
            ref={ref}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
        >
            <Stack spacing={1}>
                <Typography variant={"body1"}>Select the assignments to show in the assignments list.</Typography>
                <Typography variant={"body1"}>
                    <Typography fontWeight={"bold"} fontStyle={"italic"} display={"inline"} component={"span"}>
                        (R)
                    </Typography>{" "}
                    indicates in the assignments roster
                </Typography>
                <QueryStatusRouter queryHook={utilizationCodesList}>
                    <form.Field name={"activeUtilizationCodeId"}>
                        {(field) => (
                            <Select
                                label={"Utilization Code"}
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e!)}
                                options={utilizationCodesList.data?.map((x) => ({
                                    label: `${x.utilCode} - ${x.utilDescription}`,
                                    value: x.utilizationCodeId,
                                }))}
                                required
                            />
                        )}
                    </form.Field>
                </QueryStatusRouter>
                <QueryStatusRouter queryHook={profileList}>
                    <Stack spacing={1}>
                        {profileList.data?.map((x, i) => (
                            <FormControlLabel
                                key={i}
                                control={
                                    <Checkbox
                                        checked={selectedAssignments.includes(x.assignmentId!)}
                                        onChange={() => handleSelectionChange(x.assignmentId!)}
                                    />
                                }
                                label={x.displayName}
                            />
                        ))}
                    </Stack>
                </QueryStatusRouter>
            </Stack>
        </form>
    );
}
